import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useParadigm } from '../../contexts/ParadigmContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Users, Shield, UserCog, ClipboardList, Plus, Trash2, Edit2,
  ChevronRight, ChevronDown, Search, X, Check, AlertCircle,
  Building2, UserPlus, UserMinus, Lock, Package, Mail, Loader2, Copy
} from 'lucide-react';
import { getAllEAEmailAllocations, allocateEAEmails, getEAEmailSuggestions, getEmailDirectory } from '../../services/emailAddressService';
import { getPrimaryEmailDomain } from '../../services/emailDomainService';

// ============================================
// TEAMS TAB COMPONENT
// ============================================
function TeamsTab({ organizationId, currentUser, allUsers, allExecutives, onRefresh }) {
  const { labels } = useParadigm();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [teamsLockedByPackage, setTeamsLockedByPackage] = useState(false);
  const [activePackageName, setActivePackageName] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showAssignExecutivesModal, setShowAssignExecutivesModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Form states
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [executiveSearchQuery, setExecutiveSearchQuery] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [selectedExecutiveIds, setSelectedExecutiveIds] = useState([]);

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${getApiBase()}/teams`,
        { headers: getAuthHeaders() }
      );
      setTeams(response.data.teams || []);
      setTeamsLockedByPackage(response.data.teams_locked_by_package || false);
      setActivePackageName(response.data.active_package_name || '');
      setError(null);
    } catch (err) {
      setError('Failed to load teams');
      console.error('Error fetching teams:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleCreateTeam = async () => {
    try {
      await axios.post(
        `${getApiBase()}/teams`,
        {
          name: newTeamName,
          description: newTeamDescription,
          member_ids: selectedMemberIds,
          executive_ids: selectedExecutiveIds
        },
        { headers: getAuthHeaders() }
      );
      setShowCreateModal(false);
      setNewTeamName('');
      setNewTeamDescription('');
      setSelectedMemberIds([]);
      setSelectedExecutiveIds([]);
      fetchTeams();
    } catch (err) {
      console.error('Error creating team:', err);
      alert('Failed to create team');
    }
  };

  const handleUpdateTeam = async () => {
    if (!selectedTeam) return;
    try {
      await axios.put(
        `${getApiBase()}/teams/${selectedTeam.id}`,
        {
          name: newTeamName,
          description: newTeamDescription
        },
        { headers: getAuthHeaders() }
      );
      setShowEditModal(false);
      setSelectedTeam(null);
      fetchTeams();
    } catch (err) {
      console.error('Error updating team:', err);
      alert('Failed to update team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await axios.delete(
        `${getApiBase()}/teams/${teamId}`,
        { headers: getAuthHeaders() }
      );
      fetchTeams();
    } catch (err) {
      console.error('Error deleting team:', err);
      alert('Failed to delete team');
    }
  };

  const handleAddMember = async (userId, role = 'member') => {
    if (!selectedTeam) return;
    try {
      await axios.post(
        `${getApiBase()}/teams/${selectedTeam.id}/members`,
        { user_id: userId, role },
        { headers: getAuthHeaders() }
      );
      fetchTeams();
      // Update selected team with new data
      const updatedTeam = teams.find(t => t.id === selectedTeam.id);
      if (updatedTeam) setSelectedTeam(updatedTeam);
    } catch (err) {
      console.error('Error adding member:', err);
      alert(err.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      await axios.delete(
        `${getApiBase()}/teams/${teamId}/members/${userId}`,
        { headers: getAuthHeaders() }
      );
      fetchTeams();
    } catch (err) {
      console.error('Error removing member:', err);
      alert('Failed to remove member');
    }
  };

  const handleUpdateExecutives = async () => {
    if (!selectedTeam) return;
    try {
      await axios.put(
        `${getApiBase()}/teams/${selectedTeam.id}/executives`,
        { executive_ids: selectedExecutiveIds },
        { headers: getAuthHeaders() }
      );
      setShowAssignExecutivesModal(false);
      fetchTeams();
    } catch (err) {
      console.error('Error updating executives:', err);
      alert('Failed to update executives');
    }
  };

  const openEditModal = (team) => {
    setSelectedTeam(team);
    setNewTeamName(team.name);
    setNewTeamDescription(team.description || '');
    setShowEditModal(true);
  };

  const openAddMemberModal = (team) => {
    setSelectedTeam(team);
    setMemberSearchQuery('');
    setShowAddMemberModal(true);
  };

  const openAssignExecutivesModal = (team) => {
    setSelectedTeam(team);
    setSelectedExecutiveIds(team.executive_ids || []);
    setExecutiveSearchQuery('');
    setShowAssignExecutivesModal(true);
  };

  // Filter users not already in the team for add member modal
  const availableMembers = useMemo(() => {
    if (!selectedTeam) return allUsers;
    const existingMemberIds = new Set(selectedTeam.members?.map(m => m.user_id) || []);
    return allUsers.filter(u => !existingMemberIds.has(u.id));
  }, [allUsers, selectedTeam]);

  const filteredAvailableMembers = useMemo(() => {
    if (!memberSearchQuery.trim()) return availableMembers;
    const query = memberSearchQuery.toLowerCase();
    return availableMembers.filter(u =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query)
    );
  }, [availableMembers, memberSearchQuery]);

  const filteredExecutives = useMemo(() => {
    if (!executiveSearchQuery.trim()) return allExecutives;
    const query = executiveSearchQuery.toLowerCase();
    return allExecutives.filter(e =>
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(query) ||
      e.email?.toLowerCase().includes(query)
    );
  }, [allExecutives, executiveSearchQuery]);

  if (loading) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className={`text-lg font-semibold text-slate-800`}>Teams</h3>
          <p className={`text-sm text-slate-500`}>{'Create teams of ' + labels.eas + ' who share access to ' + labels.executives.toLowerCase()}</p>
        </div>
        {!teamsLockedByPackage && (
          <Button onClick={() => { setShowCreateModal(true); setNewTeamName(''); setNewTeamDescription(''); setSelectedMemberIds([]); setSelectedExecutiveIds([]); }}>
            <Plus className="w-4 h-4 mr-2" />
            Create Team
          </Button>
        )}
      </div>

      {/* Teams Locked by Package Notice */}
      {teamsLockedByPackage && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-full">
                <Lock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 mb-1">
                Teams Not Available in Current Package
              </h4>
              <p className="text-sm text-amber-700 mb-2">
                Team management is not included in the assigned package:
              </p>
              <div className="flex items-center space-x-2 bg-amber-100 rounded-md px-3 py-2 inline-flex">
                <Package className="h-4 w-4 text-amber-700" />
                <span className="font-medium text-amber-800">{activePackageName || 'Unknown Package'}</span>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Contact your administrator to upgrade to a package that includes Teams.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Teams List */}
      <div className="space-y-3">
        {teams.length === 0 ? (
          <div className={`text-center py-12 rounded-lg bg-slate-50`}>
            <Users className={`w-12 h-12 mx-auto mb-3 text-slate-300`} />
            <p className='text-slate-500'>No teams created yet</p>
            {!teamsLockedByPackage && (
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create your first team
              </Button>
            )}
          </div>
        ) : (
          teams.map(team => (
            <div key={team.id} className={`border rounded-lg overflow-hidden border-slate-200 bg-white`}>
              {/* Team Header */}
              <div
                className={`flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50`}
                onClick={() => setExpandedTeamId(expandedTeamId === team.id ? null : team.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedTeamId === team.id ? (
                    <ChevronDown className={`w-5 h-5 text-slate-400`} />
                  ) : (
                    <ChevronRight className={`w-5 h-5 text-slate-400`} />
                  )}
                  <div>
                    <h4 className={`font-medium text-slate-800`}>{team.name}</h4>
                    {team.description && <p className={`text-sm text-slate-500`}>{team.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className='bg-blue-50 text-blue-700'>
                      <Users className="w-3 h-3 mr-1" />
                      {team.members?.length || 0} members
                    </Badge>
                    <Badge variant="secondary" className='bg-green-50 text-green-700'>
                      <UserCog className="w-3 h-3 mr-1" />
                      {team.executive_ids?.length || 0} {labels.executives.toLowerCase()}
                    </Badge>
                  </div>
                  {!teamsLockedByPackage && (
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(team)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className='text-red-600 hover:text-red-700 hover:bg-red-50' onClick={() => handleDeleteTeam(team.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedTeamId === team.id && (
                <div className={`border-t p-4 border-slate-200 bg-slate-50`}>
                  <div className="grid grid-cols-2 gap-6">
                    {/* Members Section */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className={`font-medium text-slate-700`}>Members</h5>
                        {!teamsLockedByPackage && (
                          <Button variant="outline" size="sm" onClick={() => openAddMemberModal(team)}>
                            <UserPlus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {team.members?.length === 0 ? (
                          <p className={`text-sm italic text-slate-400`}>No members</p>
                        ) : (
                          team.members?.map(member => (
                            <div key={member.user_id} className={`flex items-center justify-between p-2 rounded border bg-white border-slate-200`}>
                              <div>
                                <span className={`text-sm font-medium text-slate-700`}>{member.user_name}</span>
                                <span className={`text-xs ml-2 text-slate-400`}>{member.user_email}</span>
                                {member.role === 'admin' && (
                                  <Badge variant="outline" className="ml-2 text-xs">Admin</Badge>
                                )}
                              </div>
                              {!teamsLockedByPackage && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-600"
                                  onClick={() => handleRemoveMember(team.id, member.user_id)}
                                >
                                  <UserMinus className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Executives Section */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className={`font-medium text-slate-700`}>{'Assigned ' + labels.executives}</h5>
                        {!teamsLockedByPackage && (
                          <Button variant="outline" size="sm" onClick={() => openAssignExecutivesModal(team)}>
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {team.executive_ids?.length === 0 ? (
                          <p className={`text-sm italic text-slate-400`}>{'No ' + labels.executives.toLowerCase() + ' assigned'}</p>
                        ) : (
                          team.executive_names?.map((name, idx) => (
                            <div key={team.executive_ids[idx]} className={`p-2 rounded border bg-white border-slate-200`}>
                              <span className={`text-sm text-slate-700`}>{name}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Team Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
            <DialogDescription>{'Create a team of ' + labels.eas + ' who will share access to assigned ' + labels.executives.toLowerCase() + '.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                value={newTeamName}
                onChange={e => setNewTeamName(e.target.value)}
                placeholder={'e.g., ' + labels.executive + ' Travel Team'}
              />
            </div>
            <div>
              <Label htmlFor="team-description">Description (optional)</Label>
              <Input
                id="team-description"
                value={newTeamDescription}
                onChange={e => setNewTeamDescription(e.target.value)}
                placeholder="Brief description of the team's purpose"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTeam} disabled={!newTeamName.trim()}>Create Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-team-name">Team Name</Label>
              <Input
                id="edit-team-name"
                value={newTeamName}
                onChange={e => setNewTeamName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="edit-team-description">Description</Label>
              <Input
                id="edit-team-description"
                value={newTeamDescription}
                onChange={e => setNewTeamDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateTeam} disabled={!newTeamName.trim()}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Modal */}
      <Dialog open={showAddMemberModal} onOpenChange={setShowAddMemberModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Add a user to {selectedTeam?.name}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={memberSearchQuery}
                onChange={e => setMemberSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredAvailableMembers.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No available users found</p>
              ) : (
                filteredAvailableMembers.map(user => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer"
                    onClick={() => { handleAddMember(user.id); setShowAddMemberModal(false); }}
                  >
                    <div>
                      <span className="font-medium text-slate-700">{user.first_name} {user.last_name}</span>
                      <span className="text-sm text-slate-400 ml-2">{user.email}</span>
                    </div>
                    <Plus className="w-4 h-4 text-slate-400" />
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Executives Modal */}
      <Dialog open={showAssignExecutivesModal} onOpenChange={setShowAssignExecutivesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{labels.assignExecutives}</DialogTitle>
            <DialogDescription>{'Select ' + labels.executives.toLowerCase() + ' that '}{selectedTeam?.name}{' members can access'}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={'Search ' + labels.executives.toLowerCase() + '...'}
                value={executiveSearchQuery}
                onChange={e => setExecutiveSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredExecutives.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">{labels.noExecutives}</p>
              ) : (
                filteredExecutives.map(exec => {
                  const isSelected = selectedExecutiveIds.includes(exec.id);
                  return (
                    <div
                      key={exec.id}
                      className={`flex items-center justify-between p-3 rounded cursor-pointer ${isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedExecutiveIds(selectedExecutiveIds.filter(id => id !== exec.id));
                        } else {
                          setSelectedExecutiveIds([...selectedExecutiveIds, exec.id]);
                        }
                      }}
                    >
                      <div>
                        <span className="font-medium text-slate-700">{exec.first_name} {exec.last_name}</span>
                        {exec.email && <span className="text-sm text-slate-400 ml-2">{exec.email}</span>}
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignExecutivesModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateExecutives}>Save Assignments</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// USER ASSIGNMENTS TAB COMPONENT
// ============================================
function UserAssignmentsTab({ organizationId, allUsers, allExecutives }) {
  const { labels } = useParadigm();
  const [userPermissions, setUserPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [executiveSearchQuery, setExecutiveSearchQuery] = useState('');
  const [selectedExecutiveIds, setSelectedExecutiveIds] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const fetchUserPermissions = async (userId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${getApiBase()}/users/${userId}/effective-permissions`,
        { headers: getAuthHeaders() }
      );
      setUserPermissions(prev => ({
        ...prev,
        [userId]: response.data
      }));
    } catch (err) {
      console.error('Error fetching user permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignModal = async (user) => {
    setSelectedUser(user);
    // Fetch current assignments
    try {
      const response = await axios.get(
        `${getApiBase()}/users/${user.id}/executive-assignments`,
        { headers: getAuthHeaders() }
      );
      setSelectedExecutiveIds(response.data.executive_ids || []);
    } catch (err) {
      setSelectedExecutiveIds([]);
    }
    setExecutiveSearchQuery('');
    setShowAssignModal(true);
  };

  const handleSaveAssignments = async () => {
    if (!selectedUser) return;
    try {
      await axios.put(
        `${getApiBase()}/users/${selectedUser.id}/executive-assignments`,
        selectedExecutiveIds,
        { headers: getAuthHeaders() }
      );
      setShowAssignModal(false);
      // Refresh permissions for this user
      fetchUserPermissions(selectedUser.id);
    } catch (err) {
      console.error('Error saving assignments:', err);
      alert('Failed to save assignments');
    }
  };

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return allUsers;
    const query = userSearchQuery.toLowerCase();
    return allUsers.filter(u =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query)
    );
  }, [allUsers, userSearchQuery]);

  const filteredExecutives = useMemo(() => {
    if (!executiveSearchQuery.trim()) return allExecutives;
    const query = executiveSearchQuery.toLowerCase();
    return allExecutives.filter(e =>
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(query) ||
      e.email?.toLowerCase().includes(query)
    );
  }, [allExecutives, executiveSearchQuery]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className={`text-lg font-semibold text-slate-800`}>{'User ' + labels.executive + ' Assignments'}</h3>
        <p className={`text-sm text-slate-500`}>{'Directly assign ' + labels.executives.toLowerCase() + ' to individual users (separate from team assignments)'}</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400`} />
        <Input
          placeholder="Search users..."
          value={userSearchQuery}
          onChange={e => setUserSearchQuery(e.target.value)}
          className={`pl-10 `}
        />
      </div>

      {/* Users Table */}
      <div className={`border rounded-lg overflow-hidden border-slate-200`}>
        <table className={`min-w-full divide-y divide-slate-200`}>
          <thead className='bg-slate-50'>
            <tr>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase text-slate-500`}>User</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase text-slate-500`}>Role</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase text-slate-500`}>Direct Assignments</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase text-slate-500`}>Team Access</th>
              <th className={`px-6 py-3 text-right text-xs font-medium uppercase text-slate-500`}>Actions</th>
            </tr>
          </thead>
          <tbody className={`divide-y bg-white divide-slate-200`}>
            {filteredUsers.map(user => {
              const permissions = userPermissions[user.id];
              return (
                <tr key={user.id} className='hover:bg-slate-50'>
                  <td className="px-6 py-4">
                    <div>
                      <div className={`font-medium text-slate-800`}>{user.first_name} {user.last_name}</div>
                      <div className={`text-sm text-slate-500`}>{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={user.is_organization_admin ? 'default' : 'secondary'}>
                      {user.is_organization_admin ? 'Org Admin' : user.role === 'ea' ? labels.ea : user.role?.toUpperCase() || labels.ea}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    {permissions ? (
                      <span className={`text-sm text-slate-600`}>
                        {permissions.direct_assignments?.executive_ids?.length || 0} {labels.executives.toLowerCase()}
                      </span>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => fetchUserPermissions(user.id)}>
                        Load
                      </Button>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {permissions ? (
                      <span className={`text-sm text-slate-600`}>
                        {permissions.team_memberships?.length || 0} teams
                      </span>
                    ) : (
                      <span className={`text-sm text-slate-400`}>-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button variant="outline" size="sm" onClick={() => handleOpenAssignModal(user)}>
                      <Edit2 className="w-4 h-4 mr-1" />
                      Assign
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Assign Executives Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{labels.assignExecutives + ' to ' + (selectedUser?.first_name || '') + ' ' + (selectedUser?.last_name || '')}</DialogTitle>
            <DialogDescription>{'Direct ' + labels.executive.toLowerCase() + ' assignments (in addition to team-based access)'}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={'Search ' + labels.executives.toLowerCase() + '...'}
                value={executiveSearchQuery}
                onChange={e => setExecutiveSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredExecutives.map(exec => {
                const isSelected = selectedExecutiveIds.includes(exec.id);
                return (
                  <div
                    key={exec.id}
                    className={`flex items-center justify-between p-3 rounded cursor-pointer ${isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedExecutiveIds(selectedExecutiveIds.filter(id => id !== exec.id));
                      } else {
                        setSelectedExecutiveIds([...selectedExecutiveIds, exec.id]);
                      }
                    }}
                  >
                    <div>
                      <span className="font-medium text-slate-700">{exec.first_name} {exec.last_name}</span>
                      {exec.email && <span className="text-sm text-slate-400 ml-2">{exec.email}</span>}
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button onClick={handleSaveAssignments}>Save Assignments</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// MANAGER RELATIONSHIPS TAB COMPONENT
// ============================================
function ManagerRelationshipsTab({ organizationId, allUsers }) {
  const [managerData, setManagerData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedManagedIds, setSelectedManagedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchAllManagerData = useCallback(async () => {
    setLoading(true);
    const data = {};
    for (const user of allUsers) {
      try {
        const response = await axios.get(
          `${getApiBase()}/users/${user.id}/managed-users`,
          { headers: getAuthHeaders() }
        );
        if (response.data.managed_user_ids?.length > 0) {
          data[user.id] = response.data;
        }
      } catch (err) {
        // Ignore errors for individual users
      }
    }
    setManagerData(data);
    setLoading(false);
  }, [allUsers]);

  useEffect(() => {
    if (allUsers.length > 0) {
      fetchAllManagerData();
    }
  }, [allUsers, fetchAllManagerData]);

  const handleOpenAssignModal = async (user) => {
    setSelectedManager(user);
    try {
      const response = await axios.get(
        `${getApiBase()}/users/${user.id}/managed-users`,
        { headers: getAuthHeaders() }
      );
      setSelectedManagedIds(response.data.managed_user_ids || []);
    } catch (err) {
      setSelectedManagedIds([]);
    }
    setSearchQuery('');
    setShowAssignModal(true);
  };

  const handleSaveRelationships = async () => {
    if (!selectedManager) return;
    try {
      await axios.put(
        `${getApiBase()}/users/${selectedManager.id}/managed-users`,
        selectedManagedIds,
        { headers: getAuthHeaders() }
      );
      setShowAssignModal(false);
      fetchAllManagerData();
    } catch (err) {
      console.error('Error saving relationships:', err);
      alert(err.response?.data?.detail || 'Failed to save relationships');
    }
  };

  // Build hierarchical tree
  const buildHierarchy = () => {
    // Find users who are managed by someone
    const managedUserIds = new Set();
    Object.values(managerData).forEach(data => {
      (data.managed_user_ids || []).forEach(id => managedUserIds.add(id));
    });

    // Top-level managers (users who manage others but aren't managed themselves)
    const topLevelManagers = allUsers.filter(u =>
      managerData[u.id] && !managedUserIds.has(u.id)
    );

    // Users who aren't in any hierarchy
    const unassignedUsers = allUsers.filter(u =>
      !managerData[u.id] && !managedUserIds.has(u.id)
    );

    return { topLevelManagers, unassignedUsers };
  };

  const renderUserNode = (user, level = 0) => {
    const managed = managerData[user.id];
    const managedUsers = managed?.managed_users || [];

    return (
      <div key={user.id} style={{ marginLeft: level * 24 }} className="mb-2">
        <div className={`flex items-center justify-between p-3 rounded-lg ${level === 0 ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${level === 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}>
              {user.first_name?.[0]}{user.last_name?.[0]}
            </div>
            <div>
              <div className={`font-medium ${level === 0 ? 'text-slate-800' : 'text-slate-800'}`}>{user.first_name} {user.last_name}</div>
              <div className={`text-xs text-slate-500`}>{user.email}</div>
            </div>
            {managedUsers.length > 0 && (
              <Badge variant="outline" className="ml-2">
                Manages {managedUsers.length}
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => handleOpenAssignModal(user)}>
            <Edit2 className="w-4 h-4" />
          </Button>
        </div>
        {managedUsers.length > 0 && (
          <div className={`mt-2 ml-4 pl-4 border-l-2 border-slate-200`}>
            {managedUsers.map(managedUser => {
              const fullUser = allUsers.find(u => u.id === managedUser.user_id);
              if (fullUser) {
                return renderUserNode(fullUser, level + 1);
              }
              return (
                <div key={managedUser.user_id} style={{ marginLeft: (level + 1) * 24 }} className="mb-2">
                  <div className={`flex items-center p-3 rounded-lg border bg-slate-50 border-slate-200`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-slate-200 text-slate-600`}>
                      ?
                    </div>
                    <div>
                      <div className={`font-medium text-slate-800`}>{managedUser.first_name} {managedUser.last_name}</div>
                      <div className={`text-xs text-slate-500`}>{managedUser.email}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const { topLevelManagers, unassignedUsers } = buildHierarchy();

  const filteredUnassignedUsers = useMemo(() => {
    if (!searchQuery.trim()) return unassignedUsers;
    const query = searchQuery.toLowerCase();
    return unassignedUsers.filter(u =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query)
    );
  }, [unassignedUsers, searchQuery]);

  const availableToManage = useMemo(() => {
    if (!selectedManager) return allUsers;
    return allUsers.filter(u => u.id !== selectedManager.id);
  }, [allUsers, selectedManager]);

  if (loading) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className={`text-lg font-semibold text-slate-800`}>Manager Relationships</h3>
        <p className={`text-sm text-slate-500`}>Configure supervision hierarchies - managers can see work of users they manage</p>
      </div>

      {/* Hierarchy Tree */}
      {topLevelManagers.length > 0 && (
        <div>
          <h4 className={`font-medium mb-3 flex items-center gap-2 text-slate-700`}>
            <Shield className="w-4 h-4" />
            Management Hierarchy
          </h4>
          <div className="space-y-2">
            {topLevelManagers.map(manager => renderUserNode(manager, 0))}
          </div>
        </div>
      )}

      {/* Unassigned Users */}
      <div>
        <h4 className={`font-medium mb-3 flex items-center gap-2 text-slate-700`}>
          <Users className="w-4 h-4" />
          Users Not in Hierarchy ({filteredUnassignedUsers.length})
        </h4>
        <div className="relative mb-4">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400`} />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={`pl-10 `}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {filteredUnassignedUsers.slice(0, 20).map(user => (
            <div key={user.id} className={`flex items-center justify-between p-3 rounded-lg border bg-slate-50 border-slate-200`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm bg-slate-200 text-slate-600`}>
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </div>
                <div>
                  <div className={`text-sm font-medium text-slate-800`}>{user.first_name} {user.last_name}</div>
                  <div className={`text-xs text-slate-500`}>{user.email}</div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleOpenAssignModal(user)}>
                <UserCog className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        {filteredUnassignedUsers.length > 20 && (
          <p className={`text-sm mt-2 text-slate-400`}>Showing 20 of {filteredUnassignedUsers.length} users</p>
        )}
      </div>

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure Management for {selectedManager?.first_name} {selectedManager?.last_name}</DialogTitle>
            <DialogDescription>Select users that this person will manage/supervise</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="max-h-64 overflow-y-auto space-y-2">
              {availableToManage.map(user => {
                const isSelected = selectedManagedIds.includes(user.id);
                return (
                  <div
                    key={user.id}
                    className={`flex items-center justify-between p-3 rounded cursor-pointer ${isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedManagedIds(selectedManagedIds.filter(id => id !== user.id));
                      } else {
                        setSelectedManagedIds([...selectedManagedIds, user.id]);
                      }
                    }}
                  >
                    <div>
                      <span className="font-medium text-slate-700">{user.first_name} {user.last_name}</span>
                      <span className="text-sm text-slate-400 ml-2">{user.email}</span>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button onClick={handleSaveRelationships}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// AUDIT LOG TAB COMPONENT
// ============================================
function AuditLogTab({ organizationId }) {
  const { labels } = useParadigm();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterTargetType, setFilterTargetType] = useState('');
  const pageSize = 50;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pageSize,
        skip: page * pageSize
      });
      if (filterAction) params.append('action', filterAction);
      if (filterTargetType) params.append('target_type', filterTargetType);

      const response = await axios.get(
        `${getApiBase()}/permission-audit-logs?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterTargetType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionBadgeColor = (action) => {
    if (action?.includes('created')) return 'bg-green-100 text-green-700';
    if (action?.includes('deleted') || action?.includes('removed') || action?.includes('revoked')) return 'bg-red-100 text-red-700';
    if (action?.includes('added') || action?.includes('granted') || action?.includes('assigned')) return 'bg-blue-100 text-blue-700';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className={`text-lg font-semibold text-slate-800`}>Permission Audit Log</h3>
        <p className={`text-sm text-slate-500`}>Track all permission changes in your organization</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Label className={`text-xs text-slate-500`}>Action Type</Label>
          <select
            value={filterAction}
            onChange={e => { setFilterAction(e.target.value); setPage(0); }}
            className={`w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white border-slate-200 text-slate-900`}
          >
            <option value="">All Actions</option>
            <option value="team_created">Team Created</option>
            <option value="team_updated">Team Updated</option>
            <option value="team_deleted">Team Deleted</option>
            <option value="member_added">Member Added</option>
            <option value="member_removed">Member Removed</option>
            <option value="executive_assigned">Executive Assigned</option>
            <option value="executive_unassigned">Executive Unassigned</option>
            <option value="granted">Permission Granted</option>
            <option value="revoked">Permission Revoked</option>
          </select>
        </div>
        <div className="flex-1">
          <Label className={`text-xs text-slate-500`}>Target Type</Label>
          <select
            value={filterTargetType}
            onChange={e => { setFilterTargetType(e.target.value); setPage(0); }}
            className={`w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white border-slate-200 text-slate-900`}
          >
            <option value="">All Types</option>
            <option value="team">Team</option>
            <option value="user">User</option>
            <option value="permission">Permission</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className={`border rounded-lg overflow-hidden border-slate-200`}>
            <table className={`min-w-full divide-y divide-slate-200`}>
              <thead className='bg-slate-50'>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase text-slate-500`}>Timestamp</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase text-slate-500`}>Action</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase text-slate-500`}>Description</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase text-slate-500`}>Performed By</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase text-slate-500`}>Target</th>
                </tr>
              </thead>
              <tbody className={`divide-y bg-white divide-slate-200`}>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={`px-4 py-8 text-center text-slate-500`}>
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map(log => (
                    <tr key={log.id} className='hover:bg-slate-50'>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap text-slate-600`}>
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getActionBadgeColor(log.action)}`}>
                          {log.action?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-sm max-w-md truncate text-slate-700`}>
                        {log.description}
                      </td>
                      <td className={`px-4 py-3 text-sm text-slate-600`}>
                        {log.performed_by_name}
                      </td>
                      <td className={`px-4 py-3 text-sm text-slate-600`}>
                        <span className='text-slate-400'>{log.target_type}:</span> {log.target_name || log.target_id}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between">
              <span className={`text-sm text-slate-500`}>
                Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(page + 1) * pageSize >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================
// EMAIL DIRECTORY TAB COMPONENT
// ============================================
function EmailDirectoryTab() {
  const { labels } = useParadigm();
  const [directory, setDirectory] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [copiedEmail, setCopiedEmail] = useState(null);

  const fetchDirectory = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getEmailDirectory();
      setDirectory(result.directory || []);
      setSummary(result.summary || {});
      setError(null);
    } catch (err) {
      console.error('Error fetching email directory:', err);
      setError('Failed to load email directory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDirectory();
  }, [fetchDirectory]);

  const handleCopy = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const filteredDirectory = useMemo(() => {
    return directory.filter(entry => {
      const matchesSearch = !searchQuery ||
        entry.email_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.linked_to_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (entry.organization_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.routing_description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = categoryFilter === 'all' ||
        (categoryFilter === 'inbound' && entry.category.startsWith('inbound_')) ||
        (categoryFilter === 'user_login' && entry.category === 'user_login') ||
        (categoryFilter === 'executive' && entry.category.startsWith('executive_'));

      return matchesSearch && matchesCategory;
    });
  }, [directory, searchQuery, categoryFilter]);

  const getCategoryBadge = (entry) => {
    const styles = {
      'inbound_general': { bg: 'bg-violet-100 text-violet-700', label: 'General Inbox' },
      'inbound_executive': { bg: 'bg-blue-100 text-blue-700', label: labels.executive + ' Inbox' },
      'inbound_trip': { bg: 'bg-green-100 text-green-700', label: 'Trip Inbox' },
      'inbound_ea_standard': { bg: 'bg-sky-100 text-sky-700', label: labels.eaStandard },
      'inbound_ea_confidential': { bg: 'bg-amber-100 text-amber-700', label: labels.eaConfidential },
      'user_login': { bg: 'bg-slate-100 text-slate-700', label: 'User Login' },
      'executive_primary': { bg: 'bg-indigo-100 text-indigo-700', label: labels.executive + ' Primary' },
      'executive_other': { bg: 'bg-indigo-50 text-indigo-600', label: labels.executive + ' Other' },
    };
    const style = styles[entry.category] || { bg: 'bg-slate-100 text-slate-600', label: entry.label };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${style.bg}`}>
        {style.label}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold text-slate-800`}>Email Directory</h3>
          <p className={`text-sm text-slate-500`}>
            Master list of all email addresses and how they are handled
          </p>
        </div>
      </div>

      {error && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-2 bg-red-50 border border-red-200 text-red-700`}>
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Summary Cards */}
      {!loading && (
        <div className="grid grid-cols-3 gap-4">
          <div className={`rounded-lg border p-4 bg-blue-50 border-blue-200`}>
            <div className={`text-2xl font-bold text-blue-700`}>{summary.inbound_addresses || 0}</div>
            <div className={`text-sm text-blue-600`}>Inbound Addresses</div>
          </div>
          <div className={`rounded-lg border p-4 bg-slate-50 border-slate-200`}>
            <div className={`text-2xl font-bold text-slate-700`}>{summary.user_logins || 0}</div>
            <div className={`text-sm text-slate-600`}>User Login Emails</div>
          </div>
          <div className={`rounded-lg border p-4 bg-indigo-50 border-indigo-200`}>
            <div className={`text-2xl font-bold text-indigo-700`}>{summary.executive_emails || 0}</div>
            <div className={`text-sm text-indigo-600`}>Executive Emails</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400`} />
          <input
            type="text"
            placeholder="Search by email, name, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="text-sm rounded-lg border px-3 py-2 bg-white border-slate-300 text-slate-700"
        >
          <option value="all">All Types</option>
          <option value="inbound">Inbound Addresses</option>
          <option value="user_login">User Logins</option>
          <option value="executive">Executive Emails</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          <div className={`text-xs text-slate-500`}>
            Showing {filteredDirectory.length} of {directory.length} addresses
          </div>
          <div className={`border rounded-lg overflow-hidden border-slate-200`}>
            <table className="w-full">
              <thead className='bg-slate-50'>
                <tr>
                  <th className={`text-left px-4 py-3 text-sm font-medium text-slate-600`}>Email Address</th>
                  <th className={`text-left px-4 py-3 text-sm font-medium text-slate-600`}>Type</th>
                  <th className={`text-left px-4 py-3 text-sm font-medium text-slate-600`}>Organization</th>
                  <th className={`text-left px-4 py-3 text-sm font-medium text-slate-600`}>Linked To</th>
                  <th className={`text-left px-4 py-3 text-sm font-medium text-slate-600`}>How It's Handled</th>
                  <th className={`text-right px-4 py-3 text-sm font-medium text-slate-600`}>Status</th>
                </tr>
              </thead>
              <tbody className={`divide-y bg-white divide-slate-200`}>
                {filteredDirectory.map((entry, idx) => (
                  <tr key={`${entry.email_address}-${entry.category}-${idx}`} className='hover:bg-slate-50'>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className={`text-sm font-mono text-slate-800`}>
                          {entry.email_address}
                        </code>
                        <button
                          onClick={() => handleCopy(entry.email_address)}
                          className={`flex-shrink-0 text-slate-400 hover:text-slate-600`}
                          title="Copy email"
                        >
                          {copiedEmail === entry.email_address ? (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getCategoryBadge(entry)}
                    </td>
                    <td className="px-4 py-3">
                      <div className={`text-sm text-slate-700`}>
                        {entry.organization_name || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`text-sm text-slate-800`}>
                        {entry.linked_to_name || '—'}
                      </div>
                      <div className={`text-xs text-slate-500`}>
                        {entry.linked_to_type}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`text-sm text-slate-600`}>
                        {entry.routing_description}
                      </div>
                      {entry.total_emails_received > 0 && (
                        <div className={`text-xs mt-0.5 text-slate-500`}>
                          {entry.total_emails_received} emails received
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {entry.is_active ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700`}>Active</span>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700`}>Inactive</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredDirectory.length === 0 && (
                  <tr>
                    <td colSpan={6} className={`px-4 py-8 text-center text-sm text-slate-500`}>
                      {searchQuery || categoryFilter !== 'all' ? 'No matching email addresses found' : 'No email addresses configured'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================
// EA EMAIL ALLOCATION TAB COMPONENT
// ============================================
function EAEmailAllocationTab({ organizationId, allUsers }) {
  const { labels } = useParadigm();
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(null);
  const [emailDomain, setEmailDomain] = useState('veyage.io');

  // Modal states
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allocating, setAllocating] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [allocationOptions, setAllocationOptions] = useState({
    allocateStandard: true,
    allocateConfidential: true,
    standardPrefix: '',
    confidentialPrefix: ''
  });

  const fetchAllocations = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getAllEAEmailAllocations();
      setAllocations(result.allocations || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching EA email allocations:', err);
      setError(`Failed to load ${labels.ea} email allocations`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllocations();
    getPrimaryEmailDomain()
      .then(domain => setEmailDomain(domain))
      .catch(() => {}); // Keep default on error
  }, [fetchAllocations]);

  const handleCopy = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const openAllocateModal = async (user) => {
    setSelectedUser(user);
    setShowAllocateModal(true);

    // Load suggestions for this user
    try {
      const result = await getEAEmailSuggestions(user.user_id);
      setSuggestions(result);
      setAllocationOptions({
        allocateStandard: !user.standard_email,
        allocateConfidential: !user.confidential_email,
        standardPrefix: result.standard?.prefix || '',
        confidentialPrefix: result.confidential?.prefix || ''
      });
    } catch (err) {
      console.error('Error loading suggestions:', err);
    }
  };

  const handleAllocate = async () => {
    if (!selectedUser) return;
    try {
      setAllocating(true);
      await allocateEAEmails(selectedUser.user_id, {
        allocateStandard: allocationOptions.allocateStandard,
        allocateConfidential: allocationOptions.allocateConfidential,
        standardPrefix: allocationOptions.standardPrefix,
        confidentialPrefix: allocationOptions.confidentialPrefix
      });
      setShowAllocateModal(false);
      setSelectedUser(null);
      await fetchAllocations();
    } catch (err) {
      console.error('Error allocating emails:', err);
      setError(err.response?.data?.detail || 'Failed to allocate email addresses');
    } finally {
      setAllocating(false);
    }
  };

  // Filter users who don't have both emails allocated
  const usersWithoutFullAllocation = allocations.filter(
    a => !a.standard_email || !a.confidential_email
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold text-slate-800`}>{labels.eaEmail} Allocation</h3>
          <p className={`text-sm text-slate-500`}>Manage personal email addresses for EAs</p>
        </div>
      </div>

      {error && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-2 bg-red-50 border border-red-200 text-red-700`}>
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Info Box */}
      <div className={`rounded-lg p-4 border bg-blue-50 border-blue-200`}>
        <h4 className={`text-sm font-semibold mb-2 text-blue-900`}>About {labels.ea} Personal Emails</h4>
        <ul className={`text-sm space-y-1 list-disc list-inside text-blue-800`}>
          <li><strong>Standard Inbox:</strong> Items are visible to the {labels.ea}, their team members, supervisors, and org admins</li>
          <li><strong>Confidential Inbox:</strong> Items are only visible to the {labels.ea}, their supervisors, and org admins (team members cannot see)</li>
        </ul>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className={`border rounded-lg overflow-hidden border-slate-200`}>
          <table className="w-full table-fixed">
            <thead className='bg-slate-50'>
              <tr>
                <th className={`text-left px-4 py-3 text-sm font-medium w-[22%] text-slate-600`}>User</th>
                <th className={`text-left px-4 py-3 text-sm font-medium w-[28%] text-slate-600`}>Standard Email</th>
                <th className={`text-left px-4 py-3 text-sm font-medium w-[32%] text-slate-600`}>Confidential Email</th>
                <th className={`text-right px-4 py-3 text-sm font-medium w-[18%] text-slate-600`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y bg-white divide-slate-200`}>
              {allocations.map((allocation) => (
                <tr key={allocation.user_id} className='hover:bg-slate-50'>
                  <td className="px-4 py-3 overflow-hidden">
                    <div className={`font-medium truncate text-slate-900`}>{allocation.user_name}</div>
                    <div className={`text-xs truncate text-slate-500`}>{allocation.user_email}</div>
                  </td>
                  <td className="px-4 py-3 overflow-hidden">
                    {allocation.standard_email ? (
                      <div className="flex items-center gap-2 min-w-0">
                        <code className={`text-xs px-2 py-1 rounded truncate bg-blue-50 text-blue-700`} title={allocation.standard_email.email_address}>
                          {allocation.standard_email.email_address}
                        </code>
                        <button
                          onClick={() => handleCopy(allocation.standard_email.email_address)}
                          className={`text-slate-400 hover:text-slate-600`}
                        >
                          {copiedEmail === allocation.standard_email.email_address ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <span className={`text-sm text-slate-400`}>Not allocated</span>
                    )}
                  </td>
                  <td className="px-4 py-3 overflow-hidden">
                    {allocation.confidential_email ? (
                      <div className="flex items-center gap-2 min-w-0">
                        <code className={`text-xs px-2 py-1 rounded flex items-center gap-1 truncate bg-amber-50 text-amber-700`} title={allocation.confidential_email.email_address}>
                          <Lock className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{allocation.confidential_email.email_address}</span>
                        </code>
                        <button
                          onClick={() => handleCopy(allocation.confidential_email.email_address)}
                          className={`text-slate-400 hover:text-slate-600`}
                        >
                          {copiedEmail === allocation.confidential_email.email_address ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <span className={`text-sm text-slate-400`}>Not allocated</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {(!allocation.standard_email || !allocation.confidential_email) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAllocateModal(allocation)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Allocate
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Allocate Modal */}
      <Dialog open={showAllocateModal} onOpenChange={setShowAllocateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Allocate Email Addresses</DialogTitle>
            <DialogDescription>
              Allocate personal email addresses for {selectedUser?.user_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Standard Email Option */}
            {!selectedUser?.standard_email && (
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="allocateStandard"
                    checked={allocationOptions.allocateStandard}
                    onChange={(e) => setAllocationOptions(prev => ({
                      ...prev,
                      allocateStandard: e.target.checked
                    }))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="allocateStandard" className="font-medium text-slate-800 flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      Standard Inbox
                    </label>
                    <p className="text-sm text-slate-600 mt-1">
                      Shared with team members, supervisors, and org admins.
                    </p>
                    {allocationOptions.allocateStandard && (
                      <div className="mt-2 flex items-center gap-1">
                        <Input
                          value={allocationOptions.standardPrefix}
                          onChange={(e) => setAllocationOptions(prev => ({
                            ...prev,
                            standardPrefix: e.target.value
                          }))}
                          className="flex-1"
                          placeholder="email prefix"
                        />
                        <span className="text-sm text-slate-500">@{emailDomain}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Confidential Email Option */}
            {!selectedUser?.confidential_email && (
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="allocateConfidential"
                    checked={allocationOptions.allocateConfidential}
                    onChange={(e) => setAllocationOptions(prev => ({
                      ...prev,
                      allocateConfidential: e.target.checked
                    }))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="allocateConfidential" className="font-medium text-slate-800 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-amber-600" />
                      Confidential Inbox
                    </label>
                    <p className="text-sm text-slate-600 mt-1">
                      Private - only visible to the {labels.ea}, supervisors, and org admins.
                    </p>
                    {allocationOptions.allocateConfidential && (
                      <div className="mt-2 flex items-center gap-1">
                        <Input
                          value={allocationOptions.confidentialPrefix}
                          onChange={(e) => setAllocationOptions(prev => ({
                            ...prev,
                            confidentialPrefix: e.target.value
                          }))}
                          className="flex-1"
                          placeholder="email prefix"
                        />
                        <span className="text-sm text-slate-500">@{emailDomain}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllocateModal(false)} disabled={allocating}>
              Cancel
            </Button>
            <Button
              onClick={handleAllocate}
              disabled={allocating || (!allocationOptions.allocateStandard && !allocationOptions.allocateConfidential)}
            >
              {allocating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Allocating...
                </>
              ) : (
                'Allocate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// MAIN ACCESS MANAGEMENT COMPONENT
// ============================================
export default function AccessManagement() {
  const { user: currentUser } = useAuth();
  const { labels } = useParadigm();
  const [allUsers, setAllUsers] = useState([]);
  const [allExecutives, setAllExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rbacStatus, setRbacStatus] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const organizationId = currentUser?.organization_id;
  const isSuperadmin = currentUser?.role === 'superadmin';
  const isOrgAdmin = currentUser?.is_organization_admin;

  // Helper to check RBAC permission
  const hasRbacPermission = (permission) => {
    if (!rbacStatus?.rbac_enabled) return false;
    const userPerms = rbacStatus?.user_permissions || [];
    if (userPerms.includes('org_admin')) return true;
    return userPerms.includes(permission);
  };

  // Determine which tabs the user can access
  const canAccessTeams = isSuperadmin || isOrgAdmin || hasRbacPermission('teams');
  const canAccessAssignments = isSuperadmin || isOrgAdmin || hasRbacPermission('user_assignments');
  const canAccessManagers = isSuperadmin || isOrgAdmin || hasRbacPermission('manager_relationships');
  const canAccessAudit = isSuperadmin || isOrgAdmin || hasRbacPermission('audit_logs');
  const canAccessEAEmails = isSuperadmin || isOrgAdmin; // Only superadmin and org admin can manage EA emails
  const canAccessEmailDirectory = isSuperadmin || isOrgAdmin; // Only superadmin and org admin can view email directory
  const hasAnyAccess = canAccessTeams || canAccessAssignments || canAccessManagers || canAccessAudit || canAccessEAEmails || canAccessEmailDirectory;

  // Get available tabs
  const getAvailableTabs = () => {
    const tabs = [];
    if (canAccessTeams) tabs.push('teams');
    if (canAccessAssignments) tabs.push('assignments');
    if (canAccessManagers) tabs.push('managers');
    if (canAccessEAEmails) tabs.push('ea-emails');
    if (canAccessEmailDirectory) tabs.push('email-directory');
    if (canAccessAudit) tabs.push('audit');
    return tabs;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch RBAC status first
        try {
          const rbacRes = await axios.get(`${getApiBase()}/rbac/status`, { headers: getAuthHeaders() });
          setRbacStatus(rbacRes.data);
        } catch (err) {
          // RBAC may not be enabled
          console.log('RBAC status not available');
        }

        const [usersRes, executivesRes] = await Promise.all([
          axios.get(`${getApiBase()}/users`, { headers: getAuthHeaders() }),
          axios.get(`${getApiBase()}/executives`, { headers: getAuthHeaders() })
        ]);
        setAllUsers(usersRes.data.users || usersRes.data || []);
        setAllExecutives(executivesRes.data.executives || executivesRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Set initial tab based on URL query param or first available tab
  useEffect(() => {
    if (loading) return;

    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const availableTabs = getAvailableTabs();

    if (tabParam && availableTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (availableTabs.length > 0 && !activeTab) {
      setActiveTab(availableTabs[0]);
    }
  }, [loading, rbacStatus, canAccessTeams, canAccessAssignments, canAccessManagers, canAccessAudit, canAccessEAEmails, canAccessEmailDirectory]);

  // Access check
  if (!loading && !hasAnyAccess) {
    return (
      <div className="p-8 text-center">
        <Shield className={`w-16 h-16 mx-auto mb-4 text-slate-300`} />
        <h2 className={`text-xl font-semibold mb-2 text-slate-700`}>Access Denied</h2>
        <p className='text-slate-500'>You need organization admin privileges to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const availableTabs = getAvailableTabs();
  const tabCount = availableTabs.length;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto bg-white rounded-lg m-4 shadow-sm">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-blue-600" />
          <h1 className={`text-2xl font-bold text-slate-800`}>Access Management</h1>
        </div>
        <div className='text-slate-500'>
          {isSuperadmin ? (
            <span className="flex items-center gap-2">
              <Badge className="bg-purple-100 text-purple-700">Superadmin</Badge>
              Managing all organizations
            </span>
          ) : isOrgAdmin ? (
            <span className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700">Org Admin</Badge>
              Managing your organization's permissions
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-700">RBAC User</Badge>
              Managing assigned permissions
            </span>
          )}
        </div>
      </div>

      {/* Tabs - only show tabs user has access to */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full mb-6`} style={{ gridTemplateColumns: `repeat(${tabCount}, 1fr)` }}>
          {canAccessTeams && (
            <TabsTrigger value="teams" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Teams
            </TabsTrigger>
          )}
          {canAccessAssignments && (
            <TabsTrigger value="assignments" className="flex items-center gap-2">
              <UserCog className="w-4 h-4" />
              User Assignments
            </TabsTrigger>
          )}
          {canAccessManagers && (
            <TabsTrigger value="managers" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Manager Relationships
            </TabsTrigger>
          )}
          {canAccessEAEmails && (
            <TabsTrigger value="ea-emails" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {labels.eaEmails}
            </TabsTrigger>
          )}
          {canAccessEmailDirectory && (
            <TabsTrigger value="email-directory" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Directory
            </TabsTrigger>
          )}
          {canAccessAudit && (
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Audit Log
            </TabsTrigger>
          )}
        </TabsList>

        <div className="rounded-lg border p-6 bg-white border-slate-200">
          {canAccessTeams && (
            <TabsContent value="teams" className="mt-0">
              <TeamsTab
                organizationId={organizationId}
                currentUser={currentUser}
                allUsers={allUsers}
                allExecutives={allExecutives}
                onRefresh={() => {}}
              />
            </TabsContent>
          )}

          {canAccessAssignments && (
            <TabsContent value="assignments" className="mt-0">
              <UserAssignmentsTab
                organizationId={organizationId}
                allUsers={allUsers}
                allExecutives={allExecutives}
              />
            </TabsContent>
          )}

          {canAccessManagers && (
            <TabsContent value="managers" className="mt-0">
              <ManagerRelationshipsTab
                organizationId={organizationId}
                allUsers={allUsers}
              />
            </TabsContent>
          )}

          {canAccessEAEmails && (
            <TabsContent value="ea-emails" className="mt-0">
              <EAEmailAllocationTab
                organizationId={organizationId}
                allUsers={allUsers}
              />
            </TabsContent>
          )}

          {canAccessEmailDirectory && (
            <TabsContent value="email-directory" className="mt-0">
              <EmailDirectoryTab />
            </TabsContent>
          )}

          {canAccessAudit && (
            <TabsContent value="audit" className="mt-0">
              <AuditLogTab organizationId={organizationId} />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
}
